import {
    BoolArrayFilter,
    ExpertAreasFilter,
    FilterClear,
    FilterGroup,
    HeaderFilter,
    IsSharedFilter,
    NotificationPriority,
    NotificationSeverity,
    PriorityFilter,
    SeverityFilter,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    useUpdateFilterPreferences,
} from "modules/common";
import { AuthorFilter } from "modules/common/components/NotificationTemplateAuthorFilter";
import { NotificationTemplatesFilter, mapToFilterName } from "modules/common/utils/filters";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { UpdateFilterPreferencesRequest } from "services";
import { RootState } from "store";

interface Props {
    authorFilter: string[];
    setAuthorFilter: React.Dispatch<React.SetStateAction<string[]>>;
    severityFilter: NotificationSeverity[];
    setSeverityFilter: React.Dispatch<React.SetStateAction<NotificationSeverity[]>>;
    priorityFilter: NotificationPriority[];
    setPriorityFilter: React.Dispatch<React.SetStateAction<NotificationPriority[]>>;
    professionalFieldFilter: string[];
    setProfessionalFieldFilter: React.Dispatch<React.SetStateAction<string[]>>;
    isSharedFilter: BoolArrayFilter[];
    setIsSharedFilter: (value: React.SetStateAction<BoolArrayFilter[]>) => void;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    searchFilter: string;
    setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
}

export const NotificationTemplatesFilters: React.FC<Props> = ({
    authorFilter,
    setAuthorFilter,
    severityFilter,
    setSeverityFilter,
    priorityFilter,
    setPriorityFilter,
    professionalFieldFilter,
    setProfessionalFieldFilter,
    isSharedFilter,
    setIsSharedFilter,
    setCurrentPage,
    searchFilter,
    setSearchFilter,
}) => {
    // expert areas
    const { expertAreas, isLoading: isLoadingExpertAreas } = useSelector(
        (state: RootState) => state.expertAreas
    );

    // dropdown states
    const [showAuthorFilter, setShowAuthorFilter] = useState(false);
    const [showSeverityFilter, setShowSeverityFilter] = useState(false);
    const [showPriorityFilter, setShowPriorityFilter] = useState(false);
    const [showProfessionalFieldFilter, setShowProfessionalFieldFilter] = useState(false);
    const [showIsSharedFilter, setShowIsSharedFilter] = useState(false);

    // refs
    const authorFilterRef = useRef<HTMLDivElement>(null);
    const severityFilterRef = useRef<HTMLDivElement>(null);
    const priorityFilterRef = useRef<HTMLDivElement>(null);
    const professionalFieldFilterRef = useRef<HTMLDivElement>(null);
    const isSharedFilterRef = useRef<HTMLDivElement>(null);

    // outside click
    useOutsideClick(authorFilterRef, () => setShowAuthorFilter(false));
    useOutsideClick(severityFilterRef, () => setShowSeverityFilter(false));
    useOutsideClick(priorityFilterRef, () => setShowPriorityFilter(false));
    useOutsideClick(professionalFieldFilterRef, () => setShowProfessionalFieldFilter(false));
    useOutsideClick(isSharedFilterRef, () => setShowIsSharedFilter(false));

    const filterPreferencesQuery = useGetFilterPreferencesQuery([
        AUTHOR_FILTER_NAME,
        SEVERITY_FILTER_NAME,
        PRIORITY_FILTER_NAME,
        PROFESSIONAL_FIELD_FILTER_NAME,
        IS_SHARED_FILTER_NAME,
    ]);

    // query
    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    useEffect(() => {
        if (filterPreferencesQuery.some(s => s.isLoading)) return;

        const authorFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === AUTHOR_FILTER_NAME
        )?.data?.filterStringValues;

        const severityFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === SEVERITY_FILTER_NAME
        )?.data?.filterIntValues;

        const priorityFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === PRIORITY_FILTER_NAME
        )?.data?.filterIntValues;

        const professionalFieldFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === PROFESSIONAL_FIELD_FILTER_NAME
        )?.data?.filterStringValues;

        const isSharedFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === IS_SHARED_FILTER_NAME
        )?.data?.filterIntValues;

        if (authorFilterData) setAuthorFilter(authorFilterData);

        if (severityFilterData) setSeverityFilter(severityFilterData);

        if (priorityFilterData) setPriorityFilter(priorityFilterData);

        if (professionalFieldFilterData) setProfessionalFieldFilter(professionalFieldFilterData);

        if (isSharedFilterData) setIsSharedFilter(isSharedFilterData);
    }, [filterPreferencesQuery]);

    // handle filter change based on filter type
    const handleUpdateFilterPreferences = (
        filterName: string,
        filterIntValue?: number,
        filterStringValue?: string,
        filterBoolValue?: boolean,
        clearFilter?: boolean
    ) => {
        const payload: UpdateFilterPreferencesRequest = {
            filterName,
            ...(filterIntValue !== undefined && { filterIntValue }),
            ...(filterStringValue !== undefined && { filterStringValue }),
            ...(filterBoolValue !== undefined && { filterBoolValue }),
            ...(clearFilter !== undefined && { clearFilter }),
        };
        updateFilterPreferencesMutation.mutate(payload);
        setCurrentPage(1);
    };

    // handle filter change
    const handleAuthorFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        handleUpdateFilterPreferences(AUTHOR_FILTER_NAME, undefined, event.target.value);
    const handleSeverityFilterChange = (severity: NotificationSeverity) =>
        handleUpdateFilterPreferences(SEVERITY_FILTER_NAME, severity);
    const handlePriorityFilterChange = (priority: NotificationPriority) =>
        handleUpdateFilterPreferences(PRIORITY_FILTER_NAME, priority);
    const handleProfessionalFieldFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        handleUpdateFilterPreferences(
            PROFESSIONAL_FIELD_FILTER_NAME,
            undefined,
            event.target.value
        );
    const handleIsSharedFilterChange = (isShared: BoolArrayFilter) =>
        handleUpdateFilterPreferences(IS_SHARED_FILTER_NAME, isShared);

    // handle filter clear
    const handleAuthorFilterClear = () =>
        handleUpdateFilterPreferences(AUTHOR_FILTER_NAME, undefined, undefined, undefined, true);
    const handleSeverityFilterClear = () =>
        handleUpdateFilterPreferences(SEVERITY_FILTER_NAME, undefined, undefined, undefined, true);
    const handlePriorityFilterClear = () =>
        handleUpdateFilterPreferences(PRIORITY_FILTER_NAME, undefined, undefined, undefined, true);
    const handleProfessionalFieldFilterClear = () =>
        handleUpdateFilterPreferences(
            PROFESSIONAL_FIELD_FILTER_NAME,
            undefined,
            undefined,
            undefined,
            true
        );
    const handleIsSharedFilterClear = () =>
        handleUpdateFilterPreferences(IS_SHARED_FILTER_NAME, undefined, undefined, undefined, true);
    const handleClearFilter = () => {
        setSearchFilter("");
        handleAuthorFilterClear();
        handleSeverityFilterClear();
        handlePriorityFilterClear();
        handleProfessionalFieldFilterClear();
        handleIsSharedFilterClear();
    };

    // handle click
    const handleAuthorFilterClick = () => setShowAuthorFilter(prev => !prev);
    const handleSeverityFilterClick = () => setShowSeverityFilter(prev => !prev);
    const handlePriorityFilterClick = () => setShowPriorityFilter(prev => !prev);
    const handleProfessionalFieldFilterClick = () => setShowProfessionalFieldFilter(prev => !prev);
    const handleIsSharedFilterClick = () => setShowIsSharedFilter(prev => !prev);

    return (
        <HeaderFilter name="notification-templates">
            <FilterGroup>
                <div ref={authorFilterRef}>
                    <AuthorFilter
                        onChange={handleAuthorFilterChange}
                        onClick={handleAuthorFilterClick}
                        onClear={handleAuthorFilterClear}
                        isActive={Boolean(authorFilter.length)}
                        showDropdown={showAuthorFilter}
                        value={authorFilter}
                    />
                </div>

                <div ref={severityFilterRef}>
                    <SeverityFilter
                        onChange={handleSeverityFilterChange}
                        onClick={handleSeverityFilterClick}
                        onClear={handleSeverityFilterClear}
                        isActive={Boolean(severityFilter.length)}
                        showDropdown={showSeverityFilter}
                        value={severityFilter}
                    />
                </div>

                <div ref={priorityFilterRef}>
                    <PriorityFilter
                        onChange={handlePriorityFilterChange}
                        onClick={handlePriorityFilterClick}
                        onClear={handlePriorityFilterClear}
                        isActive={Boolean(priorityFilter.length)}
                        showDropdown={showPriorityFilter}
                        value={priorityFilter}
                    />
                </div>

                <div ref={professionalFieldFilterRef}>
                    <ExpertAreasFilter
                        onChange={handleProfessionalFieldFilterChange}
                        onClick={handleProfessionalFieldFilterClick}
                        onClear={handleProfessionalFieldFilterClear}
                        isActive={Boolean(professionalFieldFilter.length)}
                        showDropdown={showProfessionalFieldFilter}
                        value={professionalFieldFilter}
                        options={expertAreas}
                        isLoading={isLoadingExpertAreas}
                    />
                </div>

                <div ref={isSharedFilterRef}>
                    <IsSharedFilter
                        onChange={handleIsSharedFilterChange}
                        onClick={handleIsSharedFilterClick}
                        onClear={handleIsSharedFilterClear}
                        isActive={Boolean(isSharedFilter.length)}
                        showDropdown={showIsSharedFilter}
                        value={isSharedFilter}
                    />
                </div>

                <FilterClear
                    show={Boolean(
                        searchFilter.length ||
                            authorFilter.length ||
                            severityFilter.length ||
                            priorityFilter.length ||
                            professionalFieldFilter.length ||
                            isSharedFilter.length
                    )}
                    onClearFilter={handleClearFilter}
                />
            </FilterGroup>
        </HeaderFilter>
    );
};

// map filter names
const AUTHOR_FILTER_NAME = mapToFilterName<NotificationTemplatesFilter>(
    NotificationTemplatesFilters.name,
    "authorFilter"
);
const SEVERITY_FILTER_NAME = mapToFilterName<NotificationTemplatesFilter>(
    NotificationTemplatesFilters.name,
    "severityFilter"
);
const PRIORITY_FILTER_NAME = mapToFilterName<NotificationTemplatesFilter>(
    NotificationTemplatesFilters.name,
    "priorityFilter"
);
const PROFESSIONAL_FIELD_FILTER_NAME = mapToFilterName<NotificationTemplatesFilter>(
    NotificationTemplatesFilters.name,
    "professionalFieldFilter"
);
const IS_SHARED_FILTER_NAME = mapToFilterName<NotificationTemplatesFilter>(
    NotificationTemplatesFilters.name,
    "isSharedFilter"
);
